import React, { useState, useEffect } from "react"
import { ValidationError, useForm } from "@formspree/react"
import axios from "axios"
import scrollTo from "gatsby-plugin-smoothscroll"
import * as helpers from "../../helpers/sectionclasses"
import { Link } from "gatsby"

const ContactForm = ({ content }) => {
  const sectionClasses = helpers.sectionClasses(content.sectionOptions)

  const [fieldErrors, setFieldErrors] = useState({})
  const validationRules = {
    email: val => val && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val),
    name: val => !!val,
    company: val => true,
    telephone: val => true,
    budget: val => true,
    message: val => true,
  }
  const validate = () => {
    let errors = {}
    let hasErrors = false
    for (let key of Object.keys(inputs)) {
      errors[key] = !validationRules[key](inputs[key])
      hasErrors |= errors[key]
    }
    setFieldErrors(prev => ({ ...prev, ...errors }))
    return !hasErrors
  }
  const renderFieldError = field => {
    if (fieldErrors[field]) {
      return (
        <div className="w-full flex justify-end pt-4 lrg:px-6">
          <p className="errorMsg">Please enter a valid {field}.</p>
        </div>
      )
    }
  }

  // Input Change Handling
  const [inputs, setInputs] = useState({
    email: "",
    name: "",
    company: "",
    telephone: "",
    budget: "",
    message: "",
  })

  useEffect(() => {
    // Only perform interactive validation after submit
    if (Object.keys(fieldErrors).length > 0) {
      validate()
    }
  }, [inputs])
  /* End new validation ^^^^ */

  const handleOnChange = event => {
    event.persist()
    setInputs(prev => ({
      ...prev,
      [event.target.id]: event.target.value,
    }))
  }

  // Server State Handling
  const [serverState, setServerState] = useState({
    submitting: false,
    status: null,
  })
  const handleServerResponse = (ok, msg) => {
    setServerState({
      submitting: false,
      status: { ok, msg },
    })
    if (ok) {
      setFieldErrors({})
      setInputs({
        email: "",
        name: "",
        company: "",
        telephone: "",
        budget: "",
        message: "",
      })

      typeof window !== "undefined" &&
        window.gtag("event", "conversion", {
          send_to: "AW-307981087/PLeECPblyfUCEJ_W7ZIB",
        })

    }
  }

  const handleOnSubmit = event => {
    event.preventDefault()
    if (!validate()) {
      scrollTo("#form")
      return
    }

    setServerState({ submitting: true })

    axios({
      method: "POST",
      url: "https://formspree.io/myybrqav",
      data: inputs,
    })
      .then(r => {
        const timer = setTimeout(() => {
          scrollTo("#form")
        }, 150)

        handleServerResponse(true, "Thanks!")
      })
      .catch(r => {
        handleServerResponse(false, r.response.data.error)
      })
  }

  return (
    <section
      id="form"
      className={`${
        serverState.status && serverState.status.ok ? "relative " : ""
      }${sectionClasses}`}
    >
      <div className="container">
        <form
          className={`${
            serverState.status && serverState.status.ok ? "py-48" : ""
          }`}
          onSubmit={handleOnSubmit}
          noValidate
        >
          <div className="lrg:px-6">
            <p>Work with us, work for us - we’d love to hear from you</p>
          </div>
          <div
            className={`${
              serverState.status && serverState.status.ok
                ? "hidden lrg:hidden "
                : "lrg:flex "
            }lrg:px-6 pt-10`}
          >
            <p className="text-base lrg:text-lg desk:text-xl lrg:pr-10 whitespace-nowrap">
              My name is
            </p>
            <p className="text-base tab:text-md border-b-2 w-full flex items-center note:mt-4 border-grey">
              <input
                id="name"
                name="name"
                onChange={handleOnChange}
                value={inputs.name}
                type="text"
                placeholder="enter your name"
                className="h-16 desk:h-24 w-full text-base tab:text-md bg-black border-none focus:border-black focus:ring-black appearance-none"
              />
            </p>
          </div>
          {renderFieldError("name")}

          <div
            className={`${
              serverState.status && serverState.status.ok
                ? "hidden lrg:hidden "
                : "lrg:flex "
            }lrg:px-6 pt-10`}
          >
            <p className="text-base lrg:text-lg desk:text-xl lrg:pr-10 whitespace-nowrap">
              I work for
            </p>
            <p className="text-base tab:text-md border-b-2 w-full flex items-center note:mt-4 border-grey">
              <input
                id="company"
                name="company"
                onChange={handleOnChange}
                value={inputs.company}
                type="text"
                placeholder="add business name (optional)"
                className="h-16 desk:h-24 w-full text-base tab:text-md bg-black border-none focus:border-black focus:ring-black appearance-none"
              />
            </p>
          </div>
          {renderFieldError("company")}

          <div
            className={`${
              serverState.status && serverState.status.ok
                ? "hidden lrg:hidden "
                : "lrg:flex "
            }lrg:px-6 pt-10`}
          >
            <p className="text-base lrg:text-lg desk:text-xl lrg:pr-10 whitespace-nowrap">
              My email is
            </p>
            <p className="text-base tab:text-md border-b-2 w-full flex items-center note:mt-4 border-grey">
              <input
                id="email"
                name="email"
                onChange={handleOnChange}
                value={inputs.email}
                type="email"
                placeholder="add email address"
                className="h-16 desk:h-24 w-full text-base tab:text-md bg-black border-none focus:border-black focus:ring-black appearance-none"
              />
            </p>
          </div>
          {renderFieldError("email")}

          <div
            className={`${
              serverState.status && serverState.status.ok
                ? "hidden lrg:hidden "
                : "lrg:flex "
            }lrg:px-6 pt-10`}
          >
            <p className="text-base lrg:text-lg desk:text-xl lrg:pr-10 whitespace-nowrap">
              and my mobile is
            </p>
            <p className="text-base tab:text-md border-b-2 w-full flex items-center note:mt-4 border-grey">
              <input
                id="telephone"
                name="telephone"
                onChange={handleOnChange}
                value={inputs.telephone}
                type="telephone"
                placeholder="add phone number"
                className="h-16 desk:h-24 w-full text-base tab:text-md bg-black border-none focus:border-black focus:ring-black appearance-none"
              />
            </p>
          </div>
          {renderFieldError("telephone")}

          <div
            className={`${
              serverState.status && serverState.status.ok
                ? "hidden lrg:hidden "
                : "lrg:flex "
            }lrg:px-6 pt-10`}
          >
            <p className="text-base lrg:text-lg desk:text-xl lrg:pr-10 whitespace-nowrap">
              I have a budget of
            </p>
            <p className="text-base tab:text-md border-2 w-full flex items-center desk:mt-4 border-grey">
              <select
                name="budget"
                id="budget"
                className="h-16 desk:h-24 custom-select w-full text-base tab:text-md bg-black appearance-none px-6 pt-2 pb-4 border-none focus:border-black focus:ring-black appearance-none"
                onChange={handleOnChange}
                value={inputs.budget}
              >
                <option value="" default>
                  --
                </option>
                <option>£3,000 - £8,000</option>
                <option>£9,000 - £12,000</option>
                <option>£13,000 - £16,000</option>
                <option>£17,000 - £20,000</option>
                <option>£20,000+</option>
              </select>
            </p>
          </div>
          {renderFieldError("budget")}

          <div
            className={`${
              serverState.status && serverState.status.ok
                ? "hidden lrg:hidden"
                : ""
            } lrg:flex lrg:px-6 pt-10 flex-wrap`}
          >
            <p className="text-base lrg:text-lg desk:text-xl lrg:pr-10 whitespace-nowrap w-full">
              and some extra thoughts
            </p>
            <p className="text-base tab:text-md border-2 w-full flex items-center note:mt-4 border-grey">
              <textarea
                rows="4"
                className="w-full text-base tab:text-md bg-black appearance-none resize-none px-6 py-10 block border-none focus:border-black focus:ring-black appearance-none"
                id="message"
                name="message"
                onChange={handleOnChange}
                value={inputs.message}
              ></textarea>
            </p>
          </div>
          {renderFieldError("message")}

          <div
            className={`${
              serverState.status && serverState.status.ok
                ? "hidden lrg:hidden"
                : ""
            } lrg:flex justify-between lrg:px-6 pt-10 flex-wrap${
              serverState.submitting ? " opacity-50" : ""
            }`}
          >
            <p className="block items-center text-sm tab:text-base">
              By submitting this form I accept the <a href="/terms-conditions" target="__blank">Privacy Policy</a>
            </p>

            <button
              type="submit"
              id="submit-btn"
              className="flex items-center px-6 px-10 h-14 bg-white border-2 mt-10 tab:mt-0 text-black hover:bg-black hover:text-white hover:border-white"
              disabled={serverState.submitting}
            >
              Let’s do this!
            </button>
          </div>

          {serverState.status && !serverState.status.ok ? (
            <div className="lrg:flex justify-end lrg:px-6 pt-10 flex-wrap">
              <p className="errorMsg">{serverState.status.msg}</p>
            </div>
          ) : (
            ""
          )}
        </form>
      </div>

      {serverState.status && serverState.status.ok ? (
        <div className="absolute left-0 top-0 w-full h-full bg-white text-black">
          <div className="w-full h-full flex items-center justify-center">
            <div className="w-1/2 lrg:px-6 text-center">
              <p className="text-lg mb-6">Thanks for submitting!</p>
              <p>
                We’ve recieved your enquiry and one of our team will be in touch
                with you shortly.
              </p>
            </div>
          </div>
        </div>
      ) : (
        ""
      )}
    </section>
  )
}

export default ContactForm
